import { motion } from 'framer-motion';
import { Sparkles, Shirt, Scissors } from 'lucide-react';
import tryonBeforeImg from '@/assets/onboarding/tryon-before.jpg';
import tryonAfterImg from '@/assets/onboarding/tryon-after.jpg';

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: 0.15 + i * 0.15,
      duration: 0.5,
      type: 'spring' as const,
      damping: 22,
      stiffness: 220,
    },
  }),
};

export function TryOnMockup({ isActive = true }: { isActive?: boolean }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-b from-muted to-background flex items-center justify-center px-5 pt-3 pb-2">
      <div className="w-full max-w-[285px] space-y-2.5">
        {/* Virtual Try-On section */}
        <motion.div
          custom={0}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-card rounded-2xl shadow-lg border border-border/50 overflow-hidden"
        >
          {/* Section label */}
          <div className="px-3 pt-2.5 pb-1.5 flex items-center gap-1.5">
            <Shirt className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-semibold text-heading">Virtual Try-On</span>
          </div>

          {/* Before / After images */}
          <div className="flex mx-3 mb-3 rounded-xl overflow-hidden">
            {/* Before */}
            <div className="flex-1 relative">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={tryonBeforeImg} alt="Before" className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-1.5 left-1.5">
                <span className="text-[9px] font-medium text-muted-foreground bg-background/80 backdrop-blur-sm rounded-md px-1.5 py-0.5">Before</span>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-border/30" />

            {/* After */}
            <div className="flex-1 relative">
              <motion.div
                className="aspect-[3/4] overflow-hidden"
                initial={{ opacity: 0 }}
                animate={isActive ? { opacity: 1 } : { opacity: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <img src={tryonAfterImg} alt="After" className="w-full h-full object-cover" />
              </motion.div>
              <div className="absolute bottom-1.5 right-1.5">
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                  transition={{ delay: 0.7, type: 'spring' }}
                  className="text-[9px] font-medium text-primary bg-primary/10 backdrop-blur-sm rounded-md px-1.5 py-0.5"
                >
                  After ✨
                </motion.span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Body Gym section - separate card */}
        <motion.div
          custom={1}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-card rounded-2xl p-3 shadow-sm border border-border/50"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-heading">Body Gym</p>
              <p className="text-[9px] text-muted-foreground">AI-powered transformations</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            {['New hair', 'Fuller lips', 'Tan skin'].map((tag, i) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                transition={{ delay: 0.6 + i * 0.08, type: 'spring' }}
                className="text-[10px] font-medium text-heading bg-muted border border-border/30 rounded-full px-2.5 py-1"
              >
                {tag}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Try on clothes tags */}
        <motion.div
          custom={2}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="flex gap-2 justify-center"
        >
          {[
            { icon: Shirt, label: 'Any saved item' },
            { icon: Scissors, label: 'Custom outfit' },
          ].map((item, i) => (
            <div key={item.label} className="flex items-center gap-1.5 bg-primary/5 border border-primary/20 rounded-xl px-3 py-2">
              <item.icon className="w-3 h-3 text-primary" />
              <span className="text-[10px] font-medium text-primary">{item.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
